import type { ButtonHTMLAttributes, Ref } from "react";
import { Slot as SlotPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--r-sm)] font-display font-medium uppercase tracking-[0.05em] text-[12.5px] transition-[background,border-color,color,box-shadow,opacity] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--accent)] text-[var(--ink)] font-semibold border-[1.5px] border-[var(--accent)] hover:bg-[var(--accent-2)] hover:border-[var(--accent-2)]",
        outline:
          "border-[1.5px] border-[var(--line)] bg-transparent text-[var(--text-2)] hover:border-[var(--line-2)] hover:text-[var(--text)] hover:bg-[var(--elev)]",
        ghost:
          "border-[1.5px] border-transparent bg-transparent text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--elev)]",
        on: "border-[1.5px] border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)] hover:bg-[var(--accent-soft)]",
        danger:
          "border-[1.5px] border-[var(--accent-line)] bg-[var(--accent-soft)] text-[var(--accent)] hover:bg-[var(--accent-soft)]",
      },
      size: {
        default: "h-9 px-3 py-2",
        sm: "h-7 rounded-[var(--r-sm)] px-2.5 text-[11.5px]",
        lg: "h-10 rounded-[var(--r-sm)] px-6",
        icon: "h-[29px] w-[29px] rounded-[var(--r-sm)] tracking-normal normal-case",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    ref?: Ref<HTMLButtonElement>;
  };

export const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? SlotPrimitive.Slot : "button";

  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
};

export { buttonVariants };
